import { Button, Form, Modal } from 'react-bootstrap';
import { CategoryKey, CategoryOption, ReportCategory, ReportCreationPayload, ReportMetadata } from './ReportModels';
import { Embed, models } from 'powerbi-client';
import React, { Dispatch, FunctionComponent, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import ApiHelper from '../Shared/ApiHelper';
import Select from 'react-select';
import { UserProfileContext } from '../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

interface SaveReportModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    categoryOptions: CategoryOption[];
    selectedCategoryOptionKeys: CategoryKey[];
    setSelectedCategoryOptionKeys: Dispatch<SetStateAction<CategoryKey[]>>;
    setDescription: Dispatch<SetStateAction<string>>;
    description: string;
    report: Embed | undefined;
    isSaveAs: boolean;
    isPublic?: boolean;
    reportName?: string;
    reportMetadata?: ReportMetadata;
    setReportMetadata?: Dispatch<SetStateAction<ReportMetadata | undefined>>;
}

const SaveReportModal: FunctionComponent<SaveReportModalProps> = (props: SaveReportModalProps) => {
    const {
        showModal,
        setShowModal,
        categoryOptions,
        selectedCategoryOptionKeys,
        setSelectedCategoryOptionKeys,
        setDescription,
        description,
        report,
        isSaveAs,
        isPublic,
        reportName,
        reportMetadata,
        setReportMetadata,
    } = props;

    const [reportTitle, setReportTitle] = useState<string>(isSaveAs ? '' : reportName || '');
    const userProfile = useContext(UserProfileContext);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    const handleClose = useCallback(() => {
        setShowModal(false);
    }, [setShowModal]);

    return (
        <Modal
            scrollable
            show={showModal}
            onHide={handleClose}
            centered
            aria-labelledby="report-save-modal"
            data-testid="saveReportModal"
        >
            <Modal.Header closeButton>
                <Modal.Title id="report-save-modal">{isSaveAs ? 'Save Report As' : 'Save Report'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className="p-3">
                    <Form.Group controlId="Title" className="mb-4">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Add Title..."
                            onChange={(event) => {
                                setReportTitle(event.target.value);
                            }}
                            disabled={!isSaveAs}
                            value={!isSaveAs ? reportName || '' : reportTitle}
                        />
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label>Category</Form.Label>
                        <Select
                            isMulti
                            className="filterSelect"
                            classNamePrefix="base-select"
                            closeMenuOnSelect={false}
                            options={categoryOptions}
                            placeholder="Select Categories..."
                            onChange={(selectedOptions) => {
                                setSelectedCategoryOptionKeys(
                                    (selectedOptions || []).map((option) => ({ CategoryKey: option.value }))
                                );
                            }}
                            value={selectedCategoryOptionKeys
                                .map((categoryKey) => {
                                    const category = categoryOptions.find((option) => option.value === categoryKey.CategoryKey);
                                    return category ? { label: category.label, value: category.value } : null;
                                })
                                .filter((option): option is { label: string; value: string } => option !== null)}
                            maxMenuHeight={150}
                        />
                    </Form.Group>
                    <Form.Group controlId="Description" className="mb-4">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Add Description..."
                            maxLength={255}
                            onChange={(event) => {
                                setDescription(event.target.value);
                            }}
                            value={description}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    data-testid='saveReportButton'
                    variant="primary"
                    onClick={() => {
                        if (isSaveAs) {
                            const saveParameters: models.ISaveAsParameters = {
                                name: reportTitle,
                                targetWorkspaceId: isPublic
                                    ? userProfile.environmentConfig.pbiConfig?.authorPublicWorkspaceId
                                    : userProfile.environmentConfig.pbiConfig?.authorWorkspaceId,
                            };

                            report?.saveAs(saveParameters);
                            setShowModal(false);
                        } else {
                            // Get dataset name from the dataset GUID
                            const datasetName = userProfile.environmentConfig.pbiConfig?.datasets.find(
                                (x) => x.datasetGuid === reportMetadata?.datasetGuid
                            )?.typeName;

                            const payload: ReportCreationPayload = {
                                Name: reportName ?? '',
                                Description: description,
                                ReportId: reportMetadata?.reportId ?? '',
                                Categories: selectedCategoryOptionKeys,
                                Type: isPublic ? 'Public' : 'Custom',
                                Dataset: datasetName,
                                DatasetId: reportMetadata?.datasetGuid,
                            };

                            api.callApi(
                                instance,
                                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                process.env.REACT_APP_NET_API_URL + '/PbiReport',
                                'PUT',
                                JSON.stringify(payload)
                            ).then(async (_result: Response) => {
                                // Fetch updated report metadata
                                api.callApi(
                                    instance,
                                    [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                    process.env.REACT_APP_NET_API_URL +
                                        `/PbiReport/${reportMetadata?.workSpaceId}/${reportMetadata?.reportId}`,
                                    'GET'
                                ).then(async (result: Response) => {
                                    const newReportMetadata: ReportMetadata = await result.json();
                                    setReportMetadata && setReportMetadata(newReportMetadata);

                                    // Show success toast
                                    toast.success('Report has been saved successfully', {
                                        position: 'top-right',
                                        autoClose: 3000,
                                        transition: Slide,
                                        draggable: false,
                                        closeOnClick: false,
                                        theme: 'colored',
                                    });

                                    setShowModal(false);
                                }).catch((error) => {
                                    toast.error(`Failed to fetch updated report metadata: ${error.message || 'Please try again.'}`, {
                                        position: 'top-right',
                                        autoClose: 5000,
                                        transition: Slide,
                                        draggable: false,
                                        closeOnClick: false,
                                        theme: 'colored',
                                    });
                                });
                            }).catch((error) => {
                                toast.error(`Failed to save report: ${error.message || 'Please try again.'}`, {
                                    position: 'top-right',
                                    autoClose: 5000,
                                    transition: Slide,
                                    draggable: false,
                                    closeOnClick: false,
                                    theme: 'colored',
                                });
                            });
                        }
                    }}
                >
                    Save Report
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SaveReportModal;
